import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Download, FileText, Mail, ShieldCheck, Trash2 } from "lucide-react";
import { api, API_BASE, getToken } from "@/lib/api";
import { useAuth } from "@/context/auth-context";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { CenterSpinner } from "@/components/common/States";

const PREFS = [
  { key: "marketing_email", label: "Promotional email" },
  { key: "marketing_sms", label: "Promotional SMS" },
  { key: "marketing_push", label: "Promotional push notifications" },
];

export default function Privacy() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const me = useQuery({ queryKey: ["privacy-me"], queryFn: () => api("/privacy/me") });
  const cfg = useQuery({ queryKey: ["privacy-config"], queryFn: () => api("/privacy/config") });
  const reqs = useQuery({ queryKey: ["privacy-requests"], queryFn: () => api("/privacy/requests") });
  const [kind, setKind] = useState("access");
  const [detail, setDetail] = useState("");

  const prefs = me.data?.privacy || {};
  const io = cfg.data?.company;

  const setPref = async (key, value) => {
    try {
      await api("/privacy/marketing", { method: "PUT", json: { [key]: value } });
      qc.invalidateQueries({ queryKey: ["privacy-me"] });
    } catch (e) {
      toast.error(e.message);
    }
  };

  const download = async () => {
    try {
      const res = await fetch(`${API_BASE}/privacy/export`, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "oppad-my-data.json";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Your data export downloaded");
    } catch (e) {
      toast.error(e.message);
    }
  };

  const submitRequest = async () => {
    try {
      await api("/privacy/requests", { method: "POST", json: { kind, detail } });
      setDetail("");
      qc.invalidateQueries({ queryKey: ["privacy-requests"] });
      toast.success("Request submitted — our Information Officer will respond.");
    } catch (e) {
      toast.error(e.message);
    }
  };

  const deleteAccount = async () => {
    try {
      await api("/auth/me", { method: "DELETE" });
      toast.success("Account deleted");
      await signOut();
      navigate("/login", { replace: true });
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (me.isLoading) return <CenterSpinner />;

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="font-display text-2xl">Privacy & data</h1>
        <p className="text-sm text-muted-foreground">Manage your preferences and exercise your POPIA rights.</p>
      </div>

      <Card className="rounded-2xl p-5">
        <h2 className="font-display text-lg">Marketing preferences</h2>
        <p className="mt-1 text-sm text-muted-foreground">Order and account messages are always sent; marketing is optional.</p>
        <div className="mt-4 space-y-3">
          {PREFS.map((p) => (
            <div key={p.key} className="flex items-center justify-between rounded-xl border p-3">
              <Label>{p.label}</Label>
              <Switch checked={!!prefs[p.key]} onCheckedChange={(v) => setPref(p.key, v)} data-testid={`pref-${p.key}`} />
            </div>
          ))}
          <div className="flex items-center justify-between rounded-xl border p-3">
            <div>
              <Label>Location</Label>
              <p className="text-xs text-muted-foreground">Used to find nearby stores and improve delivery.</p>
            </div>
            <Switch checked={!!prefs.location} onCheckedChange={(v) => setPref("location", v)} data-testid="pref-location" />
          </div>
        </div>
      </Card>

      <Card className="rounded-2xl p-5">
        <h2 className="font-display text-lg">Your data</h2>
        <div className="mt-4 flex flex-wrap gap-3">
          <Button variant="outline" className="rounded-full" onClick={download} data-testid="download-data-button">
            <Download className="mr-2 h-4 w-4" /> Download my data
          </Button>
          {io && (
            <a href={`mailto:${io.information_officer_email}`}>
              <Button variant="outline" className="rounded-full" data-testid="contact-io-button">
                <Mail className="mr-2 h-4 w-4" /> Contact Information Officer
              </Button>
            </a>
          )}
        </div>
      </Card>

      <Card className="rounded-2xl p-5">
        <h2 className="font-display text-lg">Make a request</h2>
        <p className="mt-1 text-sm text-muted-foreground">Access, correct, delete, or object to processing of your personal information.</p>
        <div className="mt-4 space-y-3">
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger data-testid="request-kind-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="access">Access my information</SelectItem>
              <SelectItem value="correction">Correct my information</SelectItem>
              <SelectItem value="deletion">Delete my information</SelectItem>
              <SelectItem value="objection">Object to processing</SelectItem>
            </SelectContent>
          </Select>
          <Textarea value={detail} onChange={(e) => setDetail(e.target.value)} placeholder="Add any details (optional)…" data-testid="request-detail" />
          <Button className="rounded-full" onClick={submitRequest} data-testid="submit-request-button">Submit request</Button>
        </div>
        {reqs.data?.length > 0 && (
          <div className="mt-4 space-y-2 border-t pt-4">
            {reqs.data.map((r) => (
              <div key={r.request_id} className="flex items-center justify-between text-sm" data-testid={`request-${r.request_id}`}>
                <span className="capitalize">{r.kind}</span>
                <span className="rounded-full bg-muted px-2 py-0.5 text-xs capitalize text-muted-foreground">{r.status.replace("_", " ")}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="rounded-2xl p-5">
        <h2 className="font-display text-lg">Legal</h2>
        <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
          <Link to="/legal/policy" className="flex items-center gap-2 text-primary" data-testid="link-policy"><ShieldCheck className="h-4 w-4" /> Privacy Policy</Link>
          <Link to="/legal/terms" className="flex items-center gap-2 text-primary" data-testid="link-terms"><FileText className="h-4 w-4" /> Terms & Conditions</Link>
          <Link to="/legal/paia" className="flex items-center gap-2 text-primary" data-testid="link-paia"><FileText className="h-4 w-4" /> PAIA Manual</Link>
          <Link to="/legal/cookies" className="flex items-center gap-2 text-primary" data-testid="link-cookies"><FileText className="h-4 w-4" /> Cookies</Link>
        </div>
      </Card>

      <Card className="rounded-2xl border-destructive/40 p-5">
        <h2 className="font-display text-lg text-destructive">Delete account</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          This permanently anonymises your profile and ends your sessions. Order records may be retained in anonymised form where required by law (e.g. tax/financial records).
        </p>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" className="mt-4 rounded-full text-destructive" data-testid="delete-account-button">
              <Trash2 className="mr-2 h-4 w-4" /> Delete my account
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete your account?</AlertDialogTitle>
              <AlertDialogDescription>
                This cannot be undone. Your name, email and phone will be anonymised and you will be signed out. Some records may be kept in anonymised form to meet legal obligations.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cancel-delete">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={deleteAccount} className="bg-destructive text-destructive-foreground" data-testid="confirm-delete">
                Delete account
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Card>
    </div>
  );
}
